import { useCallback, useEffect, useRef, useState } from "react";
import ApiService from "../core/service/api-service";
import {
    BellOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    LeftOutlined,
    RightOutlined,
} from "@ant-design/icons";
import { Badge, Button, Divider, Drawer, Popover, Space, Spin, Tooltip, Typography, notification as antdNotification } from "antd";
import { t } from "i18next";
import FileHelper from "../core/utils/file-helper";
import { dateFormat } from "../core/utils/date-utils";
import { useSignalR } from "../core/context/SignalRContext";
import { useIsSmallScreen } from "../core/utils/useMediaQuery";
import "./notification-menu.css";

const PAGE_SIZE = 5;

// Sunucudaki NotificationTypeEnum / NotificationItemStatus ile birebir.
const TYPE = { DOWNLOAD_REPORT: 1, SHARE_STUDY_EMAIL: 2, STUDY_ZIP_DOWNLOAD: 3, STUDY_KOS_MAKE: 4, ADMIN_MESSAGE: 5 };
const STATUS = { CONTINUE: 1, COMPLETED: 2, ERROR: 3 };

/**
 * Bildirim zili.
 *
 * Eskiden 3 saniyede bir poll ediyordu ve liste YALNIZ popover açıkken doluyordu; bu yüzden rozet
 * hiç yanmıyor, biten arka plan görevleri ancak zile tıklayınca görülüyordu.
 *
 * Artık: açılışta bir kez okunmamış sayısı + (popover açılınca) liste; sonrası SignalR olayları.
 * Yeniden bağlanmada tam tazeleme yapılır çünkü kesinti sırasındaki olaylar kaybolur.
 */
export default function NotificationMenu() {
    const { onNotification, onReconnected, connected } = useSignalR();

    // Dar ekranda 400px sabit genişlikli bir popover ekrana sığmıyor (zil sağ üstte,
    // panel soldan taşıyor) ve içindeki satırlar kırpılıyordu → panel çekmeceye taşınır.
    // Eşik uygulamanın geri kalanıyla aynı (992).
    const isSmallScreen = useIsSmallScreen(992);

    const [notifications, setNotifications] = useState([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [page, setPage] = useState(0)
    const [visible, setVisible] = useState(false)

    // Olay geldiğinde popover kapalıysa listeyi çekmeye gerek yok; sadece sayaç tazelenir.
    const visibleRef = useRef(visible)
    visibleRef.current = visible

    const pageRef = useRef(page)
    pageRef.current = page

    // Aynı bildirim için köşe kutusu bir kez çıkar. Sunucu "değişti" olayını okundu işaretlemede
    // ve durum güncellemesinde de yolluyor; bu olmadan aynı kutu tekrar tekrar açılırdı.
    const poppedRef = useRef(new Set())

    const loadUnreadCount = useCallback(async () => {
        const res = await ApiService.Notification.GetUnreadCount()
        if (res.success)
            setUnreadCount(res.data ?? 0)
    }, [])

    const loadList = useCallback(async (targetPage) => {
        const res = await ApiService.Notification.GetNotificationList(targetPage ?? pageRef.current)
        if (res.success)
            setNotifications(res.data || [])
    }, [])

    const refresh = useCallback(async () => {
        await loadUnreadCount()
        if (visibleRef.current)
            await loadList()
    }, [loadUnreadCount, loadList])

    // Açılışta bir kez: rozet doğru sayıyla gelsin.
    useEffect(() => {
        loadUnreadCount()
    }, [loadUnreadCount])

    // Sunucudan gelen her bildirim olayı: sayaç her hâlükârda, liste yalnız açıkken.
    useEffect(() => {
        if (!onNotification?.key)
            return

        refresh()
    }, [onNotification?.key])

    // Yeniden bağlanma: kesinti sırasında kaçan olayları telafi etmek için tam tazeleme.
    useEffect(() => {
        if (!onReconnected?.key)
            return

        refresh()
    }, [onReconnected?.key])

    // Popover açılınca / sayfa değişince liste çekilir.
    useEffect(() => {
        if (visible)
            loadList(page)
    }, [visible, page, loadList])

    const getIcon = (status) => {
        if (status === STATUS.CONTINUE)
            return <Spin spinning={true} size={'small'} />
        if (status === STATUS.COMPLETED)
            return <CheckCircleOutlined twoToneColor="#52c41a" />
        if (status === STATUS.ERROR)
            return <CloseCircleOutlined />
        return null
    }

    // Bildirim kendi indirme adresini taşır (data.downloadUrl); eski kayıtlarda yoksa tipe göre
    // rapor Excel'ine düşülür. Böylece yeni bildirim tipleri UI'da tip tahminine muhtaç kalmaz.
    const parseData = (notification) => {
        try {
            return notification.data ? JSON.parse(notification.data) : null
        } catch {
            return null
        }
    }

    const download = async (notification) => {
        const data = parseData(notification)
        const res = data?.DownloadUrl || data?.downloadUrl
            ? await ApiService.Notification.DownloadByUrl(data.DownloadUrl || data.downloadUrl, notification.id)
            : await ApiService.Report.DownloadReportsExcel(notification.id)

        if (res.success) {
            FileHelper.saveFile({ blobLink: res.data, fileName: res.fileName })
            await setRead(notification.id)
        }
    }

    const getActionButton = (notification) => {
        if (notification.status !== STATUS.COMPLETED)
            return null

        if (notification.type === TYPE.ADMIN_MESSAGE)
            return null

        const data = parseData(notification)
        const hasFile = !!(data?.File || data?.file || data?.DownloadUrl || data?.downloadUrl)

        if (!hasFile)
            return null

        return <a onClick={() => download(notification)}>{t('Download')}</a>
    }

    const setRead = async (id) => {
        const res = await ApiService.Notification.SetRead(id)
        if (res.success)
            await refresh()
    }

    /**
     * Sağ üst köşe kutusu.
     *
     * Kurallar:
     *  - Yalnız SONUÇ gösterilir: "devam ediyor" sessizdir (kullanıcı butona yeni bastı, biliyor).
     *  - Aynı bildirim için bir kez (bkz. poppedRef) — okundu işaretlemesi kutuyu tekrar açmasın.
     *  - Zil menüsü açıkken çıkmaz; kullanıcı zaten listeye bakıyor.
     *  - Dosya üreten görevlerde kutunun içinden doğrudan indirilebilir.
     */
    const showArrivalPopup = (raw) => {
        if (!raw || visibleRef.current)
            return

        // Hub yükünün alan adları sunucunun JSON ayarına bağlı; iki yazımı da kabul ederiz ki
        // ayar değişirse kutu sessizce kaybolmasın.
        const payload = {
            id: raw.id ?? raw.Id,
            name: raw.name ?? raw.Name,
            description: raw.description ?? raw.Description,
            status: raw.status ?? raw.Status,
            type: raw.type ?? raw.Type,
            data: raw.data ?? raw.Data,
        }

        if (payload.id == null || payload.status === STATUS.CONTINUE)
            return

        if (poppedRef.current.has(payload.id))
            return

        poppedRef.current.add(payload.id)

        const isError = payload.status === STATUS.ERROR
        const data = parseData(payload)
        const hasFile = !isError && !!(data?.File || data?.file || data?.DownloadUrl || data?.downloadUrl)
        const key = `notification-${payload.id}`

        antdNotification[isError ? 'error' : 'success']({
            key,
            message: payload.name,
            description: payload.description || null,
            // Köşe kutusunun sabit 384px genişliği dar ekranda taşıyor: mobilde kutu
            // üstte, ekrana göre genişlikte.
            placement: isSmallScreen ? 'top' : 'topRight',
            style: isSmallScreen ? { width: 'calc(100vw - 24px)', maxWidth: 384 } : undefined,
            // Yönetici mesajı okunması gereken bir metin; görev sonucu ise kendiliğinden kapanabilir.
            duration: payload.type === TYPE.ADMIN_MESSAGE ? 10 : 6,
            btn: hasFile
                ? (
                    <Space>
                        <Button
                            size={'small'}
                            type={'primary'}
                            onClick={() => {
                                antdNotification.destroy(key)
                                download(payload)
                            }}
                        >
                            {t('Download')}
                        </Button>
                    </Space>
                )
                : null,
        })
    }

    // Popup, sayaç tazelemesinden AYRI bir efektte: biri diğerinin hatasından etkilenmesin ve
    // kutu yalnız canlı olayda çıksın (yeniden bağlanma sonrası toplu tazelemede çıkmaz).
    useEffect(() => {
        if (!onNotification?.key)
            return

        if (onNotification.event === 'NotificationChanged')
            showArrivalPopup(onNotification.payload)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onNotification?.key])

    const removeItem = async (id) => {
        const res = await ApiService.Notification.RemoveItem(id)
        if (res.success)
            await refresh()
    }

    const clear = async () => {
        const res = await ApiService.Notification.Clear()
        if (res.success) {
            setPage(0)
            await refresh()
        }
    }

    const panelTitle = (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
                {t('Notifications')}
            </Typography.Title>
            {!connected && (
                <Tooltip title={t('NOTIFICATION_DISCONNECTED')}>
                    <Typography.Text type={'warning'} style={{ fontSize: 11 }}>
                        {t('Offline')}
                    </Typography.Text>
                </Tooltip>
            )}
        </div>
    )

    const list = (
        <>
            {notifications.length === 0 && (
                <Typography.Text style={{ paddingLeft: 8 }}>
                    {t('No Task')}
                </Typography.Text>
            )}

            {notifications.map(n => (
                <div key={n.id}>
                    <div style={{ padding: 4, paddingLeft: 8, paddingRight: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Typography.Text>
                                    {n.status === STATUS.CONTINUE
                                        ? `${Math.round((new Date().getTime() - new Date(n.createdDate)) / 1000)} s`
                                        : dateFormat(n.statusDate)}
                                </Typography.Text>
                                {!n.readDate && (
                                    <div style={{ width: 8, height: 8, backgroundColor: 'red', borderRadius: 4 }} />
                                )}
                            </div>
                            {/* Dokunmatikte 24px'lik düğmeler ıskalanıyor → dar ekranda bir boy büyük. */}
                            <Space size={4}>
                                {n.status !== STATUS.CONTINUE && !n.readDate && (
                                    <Button onClick={() => setRead(n.id)} size={isSmallScreen ? 'middle' : 'small'}><CheckOutlined /></Button>
                                )}
                                {n.status !== STATUS.CONTINUE && (
                                    <Button onClick={() => removeItem(n.id)} size={isSmallScreen ? 'middle' : 'small'}><DeleteOutlined /></Button>
                                )}
                            </Space>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, minWidth: 0, flex: 1 }}>
                                {/* Görev adı uzun olabiliyor: kırpmak yerine sarsın, yoksa mobilde
                                    tek satıra sıkışıp okunamıyordu. */}
                                <Typography.Text style={{ color: 'black', fontWeight: 'bold', wordBreak: 'break-word' }}>
                                    {n.name}
                                </Typography.Text>
                                {getActionButton(n)}
                            </div>
                            {getIcon(n.status)}
                        </div>
                        {n.description && (
                            <div style={{ fontSize: '9pt', color: '#6a6d70', wordBreak: 'break-word' }}>
                                {n.description}
                            </div>
                        )}
                    </div>
                    <Divider style={{ margin: 0 }} />
                </div>
            ))}
        </>
    )

    const footer = (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            marginTop: isSmallScreen ? 0 : 8,
            paddingLeft: 8,
            paddingRight: 8
        }}>
            <Space size={4}>
                <Button
                    size={isSmallScreen ? 'middle' : 'small'}
                    disabled={page === 0}
                    icon={<LeftOutlined />}
                    onClick={(e) => { e.stopPropagation(); setPage(p => Math.max(0, p - 1)) }}
                />
                {/* Sayfa dolu değilse sonraki sayfa yoktur. (Eski kod burada page-1 yapıyordu.) */}
                <Button
                    size={isSmallScreen ? 'middle' : 'small'}
                    disabled={notifications.length < PAGE_SIZE}
                    icon={<RightOutlined />}
                    onClick={(e) => { e.stopPropagation(); setPage(p => p + 1) }}
                />
            </Space>
            <Button onClick={clear} disabled={notifications.length === 0} size={isSmallScreen ? 'middle' : 'small'}>
                {t('Clear')}
            </Button>
        </div>
    )

    const menu = (
        <div style={{ width: 400 }}>
            <div style={{ padding: '0 8px' }}>
                {panelTitle}
            </div>
            {list}
            {footer}
        </div>
    )

    /* header eylemi: şeffaf, beyaz ikonlu; okunmamış varsa sayıyla rozet */
    const bell = (onClick) => (
        <Button
            type="text"
            className="header-action"
            aria-label="Notifications"
            onClick={onClick}
            icon={
                <Badge count={unreadCount} size={'small'} offset={[1, -1]}>
                    <BellOutlined style={{ fontSize: 17 }} />
                </Badge>
            }
        />
    )

    // Dar ekran: panel sağdan açılan bir çekmece. Liste ekran yüksekliğini kullanır,
    // sayfalama/temizle çekmecenin altlığında sabit kalır.
    if (isSmallScreen)
        return (
            <>
                {bell(() => setVisible(true))}
                <Drawer
                    className={'notification-drawer'}
                    placement={'right'}
                    width={'min(94vw, 420px)'}
                    open={visible}
                    onClose={() => setVisible(false)}
                    title={panelTitle}
                    footer={footer}
                    styles={{ body: { padding: '8px 0' } }}
                >
                    {list}
                </Drawer>
            </>
        )

    return (
        <Popover
            content={menu}
            trigger={'click'}
            placement={'bottomRight'}
            onOpenChange={open => setVisible(open)}
        >
            {bell()}
        </Popover>
    )
}
